import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import Table from "../../../../core/common/dataTable/index";
import CollapseHeader from "../../../../core/common/collapse-header/collapse-header";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../../core/data/redux/store";
import { exportToCSV, exportToExcel, exportToPDF } from "../../../../core/common/GlobalFunctions/exportTableData";
import { filterData } from "../../../../core/common/GlobalFunctions/tableFilters";
import { formatDate } from "../../../../core/utils/configFormatters";
import { useAppConfig } from "../../../../customhooks/useAppConfig";
import ConfigurationModal from "../modals/configurationModal";
import { fetchEsicConfigurationList } from "../../../../core/data/redux/payroll/ESIC/Master/Configuration/configurationActions";
import dayjs from "dayjs";
import { fetchEsicBranchList } from "../../../../core/data/redux/payroll/ESIC/Master/Branch/branchAction";
import BranchModal from "../modals/branchModal";
import DisablementModal from "../modals/disablementModal";
import { fetchEsicDisablementTypeList } from "../../../../core/data/redux/payroll/ESIC/Master/Disablement/disablementAction";
import EsicReasonModal from "../modals/EsicReasonModal";
import { fetchEsicReasonList } from "../../../../core/data/redux/payroll/ESIC/Master/Reason/ReasonAction";
import { fetchEsicBenefitTypeList } from "../../../../core/data/redux/payroll/ESIC/Master/Benefit/benefitAction";
import EsicBenefitModal from "../modals/benefitModal";
import { decryptData } from "../../../../core/utils/encryptUtils";

const Configuration = () => {
    const dispatch = useDispatch<AppDispatch>();
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [tableKey, setTableKey] = useState(0);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
    const [modalType, setModalType] = useState<'edit' | 'create' | ''>('');
    const [activeTab, setActiveTab] = useState("configuration");
    const [selectedRowData, setSelectedRowData] = useState<any>(null);
    const { list: disablementList = [] } = useSelector((state: any) => state.esic.disablement);
    const { activeCompany } = useSelector((state: any) => state.companyDetails);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const { configList = [] } = useSelector((state: any) => state.esic.configuration);
    const { branchList = [] } = useSelector((state: any) => state.esic.branch);
    const { benefitTypeList = [] } = useSelector((state: any) => state.esic.benefit);
    const { list: reasonList = [] } = useSelector((state: any) => state.esic.reason);
    const [selectedFilter, setSelectedFilter] = useState<any>('Recently Added');

    // Permission
    const encryptedPermissions = localStorage.getItem('currentPermissions');
    const permissions: string[] = Array.isArray(decryptData(encryptedPermissions || '')) ? decryptData(encryptedPermissions || '') : [];

    const canAdd =
        permissions.includes("Add") || permissions.includes("AllowAll");
    const canView =
        permissions.includes("View") || permissions.includes("AllowAll");
    const canEdit =
        permissions.includes("Edit") || permissions.includes("AllowAll");
    const canDelete =
        permissions.includes("Delete") || permissions.includes("AllowAll");
    const canExport =
        permissions.includes("Export") || permissions.includes("AllowAll");

    const [reasonData, setReasonData] = useState({
        reason_type: "",
        reason_text: "",
    });
    const [benefitData, setBenefitData] = useState({
        benefit_name: "",
        description: "",
    });
    const [esicData, setEsicData] = useState({
        company_id: activeCompany?.company_name,
        esic_code: '',
        office_name: '',
        office_address: '',
        employer_contribution_rate: '',
        employee_contribution_rate: '',
        wage_limit: 0,
        effective_from: '',
        effective_to: '',
        status: 'active',
    });
    const [branchData, setBranchData] = useState({
        branch_code: '',
        branch_name: '',
        branch_address: '',
        branch_contact: '',
    });
    const [disablementData, setDisablementData] = useState({
        disablement_type: '',
        description: '',
    });

    const handleTabClick = (tab: string) => {
        setActiveTab(tab);
        setTableKey(prevKey => prevKey + 1);
    };

    useEffect(() => {
        setTableKey(prevKey => prevKey + 1);
    }, [selectedFilter, configList, reasonList, branchList, disablementList, benefitTypeList]);

    const handleEditConfigData = (record: any): void => {
        setSelectedRowData(record);
        setEsicData({
            company_id: record.company_id,
            esic_code: record.esic_code,
            office_name: record.office_name,
            office_address: record.office_address,
            employer_contribution_rate: record.employer_contribution_rate,
            employee_contribution_rate: record.employee_contribution_rate,
            wage_limit: record.wage_limit,
            effective_from: record.effective_from ? dayjs(record.effective_from).format("YYYY-MM-DD") : "",
            effective_to: record.effective_to ? dayjs(record.effective_to).format("YYYY-MM-DD") : "",
            status: record.status || 'active',
        });
        showModal('edit');
    };
    const handleEditBranchData = (record: any): void => {
        setSelectedRowData(record);
        setBranchData({
            branch_code: record.branch_code,
            branch_name: record.branch_name,
            branch_address: record.branch_address,
            branch_contact: record.branch_contact,
        });
        showModal('edit');
    };
    const handleEditDisablementData = (record: any): void => {
        setSelectedRowData(record);
        setDisablementData({
            disablement_type: record.disablement_type,
            description: record.description,
        });
        showModal('edit');
    };

    const handleEditBenefitData = (record: any): void => {
        setSelectedRowData(record);
        setBenefitData({
            benefit_name: record.benefit_name || "",
            description: record.description || "",
        });
        showModal('edit');
    };

    const handleEditReasonData = (record: any): void => {
        setSelectedRowData(record);
        setReasonData({
            reason_type: record.reason_type,
            reason_text: record.reason_text,
        });
        showModal('edit');
    };

    const showModal = (type: 'edit' | 'create') => {
        setModalType(type);
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        setModalType('');
    };
    const config = useAppConfig() ?? {};

    const exportDisablement = disablementList?.map((value: any) => ({
        "Id": value.disablement_id,
        "Disablement Type": value.disablement_type || "N/A",
        "Description": value.description || "N/A",
    }));

    const exportConfigData = configList?.map((value: any) => ({
        "Config ID": value.config_id,
        "Company ID": value.company_id,
        "ESIC Code": value.esic_code,
        "Office Name": value.office_name,
        "Office Address": value.office_address,
        "Employer Contribution Rate (%)": value.employer_contribution_rate ? `${value.employer_contribution_rate}%` : "0%",
        "Employee Contribution Rate (%)": value.employee_contribution_rate ? `${value.employee_contribution_rate}%` : "0%",
        "Wage Limit": value.wage_limit ?? 0,
        "effective_from": formatDate(value.effective_from, config),
        "effective_to": formatDate(value.effective_to, config),
        // "Status": value.status,
    }));

    const exportBranch = branchList?.map((value: any) => ({
        "Id": value.branch_id,
        "Branch Code": value.branch_code,
        "Branch Name": value.branch_name,
        "Branch Address": value.branch_address,
        "Branch Contact": value.branch_contact,
    }));

    const exportReasons = reasonList?.map((value: any) => ({
        "Id": value.reason_id,
        "Reason Type": value.reason_type,
        "Reason Text": value.reason_text,
    }));

    const exportBenefits = benefitTypeList?.map((value: any) => ({
        Id: value.benefit_id,
        "Benefit Name": value.benefit_name,
        Description: value.description,
    }));

    const getColumns = () => {
        if (activeTab === 'configuration') {
            return [
                {
                    title: "Company",
                    dataIndex: "company_id",
                    sorter: (a: any, b: any) => a.company_id.localeCompare(b.company_id),
                    render: (text: number, record: any) => (
                        <div
                            className="d-flex align-items-center"
                            style={{ gap: "8px" }}
                            onMouseEnter={(e) => e.currentTarget.querySelector("button")?.style.setProperty("opacity", "1")}
                            onMouseLeave={(e) => e.currentTarget.querySelector("button")?.style.setProperty("opacity", "0")}
                        >
                            <h6 className="fs-14 fw-medium text-gray-9 mb-0">{text}</h6>
                            <button
                                className="btn btn-link p-0"
                                onClick={() => canEdit && (showModal("edit"), handleEditConfigData(record))}
                                disabled={!canEdit}
                                style={{
                                    cursor: canEdit ? "pointer" : "not-allowed",
                                    opacity: 0,
                                    transition: "opacity 0.3s",
                                }}
                                data-bs-toggle="tooltip"
                                data-bs-placement="top"
                                title={!canEdit ? "Permission Denied" : "Edit Config Data"}
                            >
                                <i className="ti ti-pencil" />
                            </button>
                        </div>
                    ),
                },
                {
                    title: "ESIC Code",
                    dataIndex: "esic_code",
                    render: (text: string) => <span>{text}</span>,
                    sorter: (a: any, b: any) => a.esic_code.localeCompare(b.esic_code),
                },
                {
                    title: "Office Name",
                    dataIndex: "office_name",
                    render: (text: string) => <h6 className="fs-14 fw-medium text-gray-9">{text}</h6>,
                    sorter: (a: any, b: any) => a.office_name.localeCompare(b.office_name),
                },
                {
                    title: "Office Address",
                    dataIndex: "office_address",
                    render: (text: string) => <p className="fs-14 text-gray-6">{text}</p>,
                    sorter: (a: any, b: any) => a.office_address.localeCompare(b.office_address),
                },
                {
                    title: "Employer Contribution Rate (%)",
                    dataIndex: "employer_contribution_rate",
                    sorter: (a: any, b: any) => parseFloat(a.employer_contribution_rate ?? "0") - parseFloat(b.employer_contribution_rate ?? "0"),
                    render: (text: string | number) => <span>{text}%</span>,
                },
                {
                    title: "Employee Contribution Rate (%)",
                    dataIndex: "employee_contribution_rate",
                    sorter: (a: any, b: any) =>
                        parseFloat(a.employee_contribution_rate ?? "0") -
                        parseFloat(b.employee_contribution_rate ?? "0"),
                    render: (text: string | number) => <span>{text}%</span>,
                },
                {
                    title: "Wage Limit",
                    dataIndex: "wage_limit",
                    sorter: (a: any, b: any) => a.wage_limit - b.wage_limit,
                    render: (text: string | number) => <span>{text}</span>,
                },
                {
                    title: "Effective From",
                    dataIndex: "effective_from",
                    sorter: (a: any, b: any) => new Date(a.effective_from).getTime() - new Date(b.effective_from).getTime(),
                    render: (text: string) => formatDate(text, config),
                },
                {
                    title: "Effective To",
                    dataIndex: "effective_to",
                    sorter: (a: any, b: any) => new Date(a.effective_to).getTime() - new Date(b.effective_to).getTime(),
                    render: (text: string) => formatDate(text, config),
                },
                // {
                //     title: "Status",
                //     dataIndex: "status",
                //     render: (text: string | undefined) => {
                //         if (!text) return <span className="text-muted">N/A</span>;

                //         const formatted = text.charAt(0).toUpperCase() + text.slice(1);
                //         return (
                //             <span className={text === 'active' ? 'text-success' : 'text-danger'}>
                //                 {formatted}
                //             </span>
                //         );
                //     }
                // },
                {
                    title: "Action",
                    dataIndex: "action",
                    render: (_: string, record: any) => (
                        <div className="action-icon d-inline-flex">
                            <button
                                className="me-2"
                                onClick={() => { if (!canEdit) return; showModal("edit"); handleEditConfigData(record); }}
                                disabled={!canEdit}
                                style={{ cursor: canEdit ? "pointer" : "not-allowed", opacity: canEdit ? 1 : 0.5, }}
                                data-bs-toggle="tooltip"
                                data-bs-placement="top"
                                title={!canEdit ? "Permission Denied" : "Edit Config Data"}
                            >
                                <i className="ti ti-edit" />
                            </button>

                            <button
                                onClick={() => { if (!canDelete) return; setSelectedRowData(record); setIsDeleteModalVisible(true); }}
                                className="me-2"
                                disabled={!canDelete}
                                style={{ cursor: !canDelete ? "not-allowed" : "pointer", opacity: !canDelete ? 0.5 : 1, }}
                                data-bs-toggle="tooltip"
                                data-bs-placement="top"
                                title={!canDelete ? "Permission Denied" : "Delete Config Data"}
                            >
                                <i className="ti ti-trash" />
                            </button>
                        </div>
                    ),
                },
            ];
        }
        if (activeTab === 'disablement') {
            return [
                {
                    title: "Disablement Type",
                    dataIndex: "disablement_type",
                    sorter: (a: any, b: any) => a.disablement_type.localeCompare(b.disablement_type),
                },
                {
                    title: "Description",
                    dataIndex: "description",
                    sorter: (a: any, b: any) => (a.description || "").localeCompare(b.description || ""),
                },
                {
                    title: "Action",
                    dataIndex: "action",
                    render: (_: string, record: any) => (
                        <div className="action-icon d-inline-flex">
                            <button
                                className="me-2"
                                onClick={() => { showModal('edit'); handleEditDisablementData(record); }}
                                data-bs-toggle="tooltip"
                                data-bs-placement="top"
                                title="Edit"
                            >
                                <i className="ti ti-edit" />
                            </button>
                            <button
                                onClick={() => { setSelectedRowData(record); setIsDeleteModalVisible(true); }}
                                className="me-2"
                                data-bs-toggle="tooltip"
                                data-bs-placement="top"
                                title="Delete"
                            >
                                <i className="ti ti-trash" />
                            </button>
                        </div>
                    ),
                },
            ];
        }
        if (activeTab === 'branch') {
            return [
                {
                    title: "Branch Code",
                    dataIndex: "branch_code",
                    render: (text: string) => <h6 className="fs-14 fw-medium text-gray-9">{text}</h6>,
                    sorter: (a: any, b: any) => a.branch_code.localeCompare(b.branch_code),
                },
                {
                    title: "Branch Name",
                    dataIndex: "branch_name",
                    render: (text: string) => <span>{text}</span>,
                    sorter: (a: any, b: any) => a.branch_name.localeCompare(b.branch_name),
                },
                {
                    title: "Branch Address",
                    dataIndex: "branch_address",
                    render: (text: string) => <p className="fs-14 text-gray-6">{text}</p>,
                },
                {
                    title: "Branch Contact",
                    dataIndex: "branch_contact",
                    render: (text: string) => <span>{text}</span>,
                },
                {
                    title: "Action",
                    dataIndex: "action",
                    render: (_: any, record: any) => (
                        <div className="action-icon d-inline-flex">
                            <button
                                className="me-2"
                                onClick={() => { showModal('edit'); handleEditBranchData(record) }}
                                data-bs-toggle="tooltip"
                                data-bs-placement="top"
                                title="Edit"
                            >
                                <i className="ti ti-edit" />
                            </button>
                            <button
                                onClick={() => { setSelectedRowData(record); setIsDeleteModalVisible(true); }}
                                className="me-2"
                                data-bs-toggle="tooltip"
                                data-bs-placement="top"
                                title="Delete"
                            >
                                <i className="ti ti-trash" />
                            </button>
                        </div>
                    ),
                },
            ];
        }
        if (activeTab === 'reason') {
            return [
                {
                    title: "Reason Type",
                    dataIndex: "reason_type",
                    render: (text: string) => <h6 className="fs-14 fw-medium text-gray-9">{text}</h6>,
                    sorter: (a: any, b: any) => a.reason_type.localeCompare(b.reason_type),
                },
                {
                    title: "Reason Text",
                    dataIndex: "reason_text",
                    sorter: (a: any, b: any) => a.reason_text.localeCompare(b.reason_text),
                },
                {
                    title: "Action",
                    dataIndex: "action",
                    render: (text: string, record: any) => (
                        <div className="action-icon d-inline-flex">
                            <button
                                className="me-2"
                                onClick={() => { showModal('edit'); handleEditReasonData(record); }}
                                data-bs-toggle="tooltip"
                                data-bs-placement="top"
                                title="Edit"
                            >
                                <i className="ti ti-edit" />
                            </button>
                            <button
                                onClick={() => { setSelectedRowData(record); setIsDeleteModalVisible(true); }}
                                className="me-2"
                                data-bs-toggle="tooltip"
                                data-bs-placement="top"
                                title="Delete"
                            >
                                <i className="ti ti-trash" />
                            </button>
                        </div>
                    ),
                },
            ];
        }
        if (activeTab === "benefit") {
            return [
                {
                    title: "Benefit Name",
                    dataIndex: "benefit_name",
                    render: (text: string) => (
                        <h6 className="fs-14 fw-medium text-gray-9">{text}</h6>
                    ),
                    sorter: (a: any, b: any) =>
                        a.benefit_name.localeCompare(b.benefit_name),
                },
                {
                    title: "Description",
                    dataIndex: "description",
                    sorter: (a: any, b: any) => a.description.localeCompare(b.description),
                },
                {
                    title: "Action",
                    dataIndex: "action",
                    render: (text: string, record: any) => (
                        <div className="action-icon d-inline-flex">
                            <button
                                className="me-2"
                                onClick={() => {
                                    showModal("edit");
                                    handleEditBenefitData(record);
                                }}
                                data-bs-toggle="tooltip"
                                data-bs-placement="top"
                                title="Edit"
                            >
                                <i className="ti ti-edit" />
                            </button>
                            <button
                                onClick={() => {
                                    setSelectedRowData(record);
                                    setIsDeleteModalVisible(true);
                                }}
                                className="me-2"
                                data-bs-toggle="tooltip"
                                data-bs-placement="top"
                                title="Delete"
                            >
                                <i className="ti ti-trash" />
                            </button>
                        </div>
                    ),
                },
            ];
        }


        // Default columns for other tabs (e.g., earning, overtime, deductions)
        return [
            {
                title: "Company ID",
                dataIndex: "company_id",
                render: (text: number) => <h6 className="fs-14 fw-medium text-gray-9">{text}</h6>,
                sorter: (a: any, b: any) => a.company_id - b.company_id,
            },
            {
                title: "ESIC Code",
                dataIndex: "esic_code",
                render: (text: string) => <span>{text}</span>,
                sorter: (a: any, b: any) => a.esic_code.localeCompare(b.esic_code),
            },
            {
                title: "Office Name",
                dataIndex: "office_name",
                render: (text: string) => <h6 className="fs-14 fw-medium text-gray-9">{text}</h6>,
                sorter: (a: any, b: any) => a.office_name.localeCompare(b.office_name),
            },
            {
                title: "Office Address",
                dataIndex: "office_address",
                render: (text: string) => <p className="fs-14 text-gray-6">{text}</p>,
                sorter: (a: any, b: any) => a.office_address.localeCompare(b.office_address),
            },
            {
                title: "Employer Contribution Rate (%)",
                dataIndex: "employer_contribution_rate",
                sorter: (a: any, b: any) => parseFloat(a.employer_contribution_rate ?? "0") - parseFloat(b.employer_contribution_rate ?? "0"),
                render: (text: string | number) => <span>{text}%</span>,
            },
            {
                title: "Employee Contribution Rate (%)",
                dataIndex: "employee_contribution_rate",
                sorter: (a: any, b: any) =>
                    parseFloat(a.employee_contribution_rate ?? "0") -
                    parseFloat(b.employee_contribution_rate ?? "0"),
                render: (text: string | number) => <span>{text}%</span>,
            },
            {
                title: "Wage Limit",
                dataIndex: "wage_limit",
                sorter: (a: any, b: any) => a.wage_limit - b.wage_limit,
                render: (text: string | number) => <span>{text}</span>,
            },
            {
                title: "Effective From",
                dataIndex: "effective_from",
                sorter: (a: any, b: any) => new Date(a.effective_from).getTime() - new Date(b.effective_from).getTime(),
                render: (text: string) => <span>{text}</span>,
            },
            {
                title: "Effective To",
                dataIndex: "effective_to",
                sorter: (a: any, b: any) => new Date(a.effective_to).getTime() - new Date(b.effective_to).getTime(),
                render: (text: string) => <span>{text}</span>,
            },
            {
                title: "Status",
                dataIndex: "status",
                render: (text: string) => (
                    <span className={text === 'active' ? 'text-success' : 'text-danger'}>
                        {text ? text.charAt(0).toUpperCase() + text.slice(1) : ""}

                    </span>
                ),
                sorter: (a: any, b: any) => a.status.localeCompare(b.status),
            },
            {
                title: "Action",
                dataIndex: "action",
                render: (_: string, record: any) => (
                    <div className="action-icon d-inline-flex">
                        <button
                            className="me-2"
                            onClick={() => { showModal('edit'); handleEditConfigData(record); }}
                            data-bs-toggle="tooltip"
                            data-bs-placement="top"
                            title="Edit"
                        >
                            <i className="ti ti-edit" />
                        </button>
                        <button
                            onClick={() => { setSelectedRowData(record); setIsDeleteModalVisible(true); }}
                            className="me-2"
                            data-bs-toggle="tooltip"
                            data-bs-placement="top"
                            title="Delete"
                        >
                            <i className="ti ti-trash" />
                        </button>
                    </div>
                ),
            },
        ];
    };

    const getTableData = () => {
        let data = [];
        switch (activeTab) {
            case "configuration":
                data = configList;
                break;
            case "disablement":
                data = disablementList;
                break;
            case "branch":
                data = branchList;
                break;
            case "reason":
                data = reasonList;
                break;
            case "benefit":
                data = benefitTypeList;
                break;
            default:
                data = configList;
                break;
        }
        return filterData(data, {}, "CreatedAt", "CreatedAt", selectedFilter);
    };

    const getTitle = () => {
        switch (activeTab) {
            case "configuration":
                return "Configuration List";
            case "disablement":
                return "Disablement List";
            case "branch":
                return "Branch List";
            case "reason":
                return "Reason List";
            case "benefit":
                return "Benefit Type List";
            default:
                return "Configuration List";
        }
    };

    const handleExport = (type: 'csv' | 'excel' | 'pdf') => {
    switch (activeTab) {
        case "configuration":
            type === 'csv'
                ? exportToCSV(exportConfigData, 'ESIC_configuration_data')
                : type === 'excel'
                    ? exportToExcel(exportConfigData, 'ESIC_configuration_data')
                    : exportToPDF(exportConfigData, 'ESIC_configuration_data');
            break;

        case "disablement":
            type === 'csv'
                ? exportToCSV(exportDisablement, 'ESIC_disablement_data')
                : type === 'excel'
                    ? exportToExcel(exportDisablement, 'ESIC_disablement_data')
                    : exportToPDF(exportDisablement, 'ESIC_disablement_data');
            break;

        case "branch":
            type === 'csv'
                ? exportToCSV(exportBranch, 'ESICBranch_data')
                : type === 'excel'
                    ? exportToExcel(exportBranch, 'ESICBranch_data')
                    : exportToPDF(exportBranch, 'ESICBranch_data');
            break;

        case "reason":
            type === 'csv'
                ? exportToCSV(exportReasons, 'ESIC_reason_data')
                : type === 'excel'
                    ? exportToExcel(exportReasons, 'ESIC_reason_data')
                    : exportToPDF(exportReasons, 'ESIC_reason_data');
            break;

        case "benefit":
            type === 'csv'
                ? exportToCSV(exportBenefits, 'benefitType_data')
                : type === 'excel'
                    ? exportToExcel(exportBenefits, 'benefitType_data')
                    : exportToPDF(exportBenefits, 'benefitType_data');
            break;

        default:
            type === 'csv'
                ? exportToCSV(exportConfigData, 'ESIC_configuration_data')
                : type === 'excel'
                    ? exportToExcel(exportConfigData, 'ESIC_configuration_data')
                    : exportToPDF(exportConfigData, 'ESIC_configuration_data');
            break;
    }
};

    const handleSortChange = (sortOption: any) => {
        setSelectedFilter(sortOption);
    };


    useEffect(() => {
        if (activeTab === 'configuration') {
            dispatch(fetchEsicConfigurationList());
        }
        if (activeTab === 'disablement') {
            dispatch(fetchEsicDisablementTypeList());
        }
        if (activeTab === 'branch') {
            dispatch(fetchEsicBranchList());
        }
        if (activeTab === 'reason') {
            dispatch(fetchEsicReasonList())
        }
        if (activeTab === 'benefit') {
            dispatch(fetchEsicBenefitTypeList())
        }
    }, [dispatch, activeTab]);

    // ---------------------------------------------------
// Listen for AI → Open ESIC Configuration Modal
// ---------------------------------------------------
useEffect(() => {
    const handler = (event: any) => {
        const detail = event.detail || {};

        // 1️⃣ Pre-fill modal data
        setEsicData((prev: any) => ({
            ...prev,
            ...detail,
        }));

        // 2️⃣ Open modal in CREATE mode
        setModalType("create");
        setIsModalVisible(true);
    };

    window.addEventListener("open-esic-configuration-modal", handler);
    return () =>
        window.removeEventListener("open-esic-configuration-modal", handler);
}, []);


    return (
        <>
            {/* Page Wrapper */}
            <div className="page-wrapper">
                {/* Content */}
                <div className="content">
                    {/* Breadcrumb */}
                    <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
                        <div className="my-auto mb-2">
                            <h2 className="mb-1">{getTitle()}</h2>
                            <nav>
                                <ol className="breadcrumb mb-0">
                                    <li className="breadcrumb-item">
                                        <Link to={all_routes.adminDashboard}>
                                            <i className="ti ti-smart-home" />
                                        </Link>
                                    </li>
                                    <li className="breadcrumb-item">Payroll</li>
                                    <li className="breadcrumb-item active" aria-current="page">
                                        {getTitle()}
                                    </li>
                                </ol>
                            </nav>
                        </div>
                        <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
                            <div className="me-2 mb-2">
                                <div className="dropdown">
                                    <Link
                                        to="#"
                                        className="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                                        data-bs-toggle="dropdown"
                                    >
                                        <i className="ti ti-file-export me-1" />
                                        Export
                                    </Link>
                                    <ul className="dropdown-menu  dropdown-menu-end p-3">
                                        <li>
                                            <button
                                                onClick={() => handleExport('csv')}
                                                className="dropdown-item rounded-1"
                                            >
                                                <i className="ti ti-file-type-pdf me-1" />
                                                Export as CSV
                                            </button>
                                        </li>
                                        <li>
                                            <button
                                                onClick={() => handleExport('excel')}
                                                className="dropdown-item rounded-1"
                                            >
                                                <i className="ti ti-file-type-xls me-1" />
                                                Export as Excel{" "}
                                            </button>
                                        </li>
                                        <li>
                                            <button
                                                onClick={() => handleExport('pdf')}
                                                className="dropdown-item rounded-1"
                                            >
                                                <i className="ti ti-file-type-xls me-1" />
                                                Export as PDF{" "}
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div className="head-icons ms-2">
                                <CollapseHeader />
                            </div>
                        </div>
                    </div>

                    {/* Tab Selection */}
                    <div className="d-flex flex-wrap gy-2 justify-content-between my-4">
                        <div className="payroll-btns">
                            <Link to="#" onClick={() => handleTabClick("configuration")} className={`btn btn-white ${activeTab === 'configuration' ? 'active' : ''} border me-2`}>Configuration</Link>
                            <Link to="#" onClick={() => handleTabClick("disablement")} className={`btn btn-white ${activeTab === 'disablement' ? 'active' : ''} border me-2`}>Disablement</Link>
                            <Link to="#" onClick={() => handleTabClick("branch")} className={`btn btn-white ${activeTab === 'branch' ? 'active' : ''} border me-2`}>Branch</Link>
                            <Link to="#" onClick={() => handleTabClick("reason")} className={`btn btn-white ${activeTab === 'reason' ? 'active' : ''} border`}>Reason</Link>
                            <Link to="#" onClick={() => handleTabClick("benefit")} className={`btn btn-white ${activeTab === 'benefit' ? 'active' : ''} border`}>Benefit Type</Link>
                        </div>
                        <div className="mb-2">
                            {canAdd && (
                                <button
                                    onClick={() => showModal('create')}
                                    className="btn btn-primary d-flex align-items-center"
                                >
                                    <i className="ti ti-circle-plus me-2" />
                                    Add {getTitle()}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Payroll List */}
                    <div className="card">
                        <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                            <h5>{getTitle()}</h5>
                            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                                <div className="dropdown">
                                    <Link
                                        to="#"
                                        className="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                                        data-bs-toggle="dropdown"
                                    >
                                        Sort By : {selectedFilter}
                                    </Link>
                                    <ul className="dropdown-menu dropdown-menu-end p-3">
                                        <li>
                                            <Link
                                                to="#"
                                                className="dropdown-item rounded-1"
                                                onClick={() => handleSortChange('Last 7 Days')}
                                            >
                                                Last 7 Days
                                            </Link>
                                        </li>
                                        <li>
                                            <Link
                                                to="#"
                                                className="dropdown-item rounded-1"
                                                onClick={() => handleSortChange('Recently Added')}
                                            >
                                                Recently Added
                                            </Link>
                                        </li>
                                        <li>
                                            <Link
                                                to="#"
                                                className="dropdown-item rounded-1"
                                                onClick={() => handleSortChange('Ascending')}
                                            >
                                                Ascending
                                            </Link>
                                        </li>
                                        <li>
                                            <Link
                                                to="#"
                                                className="dropdown-item rounded-1"
                                                onClick={() => handleSortChange('Descending')}
                                            >
                                                Descending
                                            </Link>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div className="card-body p-0">
                            <Table key={tableKey} dataSource={getTableData()} columns={getColumns()} Selection={true} />
                        </div>
                    </div>
                    {/* /Payroll List */}
                </div>
                {/* Footer */}
                <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
                    <p className="mb-0">2024 - 2025© hrmtree.</p>
                    <p>
                        Developed By{" "}
                        <Link to="#" className="text-primary">
                            Leadchain Systems
                        </Link>
                    </p>
                </div>
            </div>
            {/* Modals */}
            {activeTab === 'configuration' && (
                <ConfigurationModal handleCancel={handleCancel} isModalVisible={isModalVisible} modalType={modalType} selectedRowData={selectedRowData} esicData={esicData} setEsicData={setEsicData} setTableKey={setTableKey} setIsDeleteModalVisible={setIsDeleteModalVisible} isDeleteModalVisible={isDeleteModalVisible} />
            )}
            {activeTab === 'branch' && (
                <BranchModal handleCancel={handleCancel} isModalVisible={isModalVisible} modalType={modalType} selectedRowData={selectedRowData} branchData={branchData} setBranchData={setBranchData} setTableKey={setTableKey} setIsDeleteModalVisible={setIsDeleteModalVisible} isDeleteModalVisible={isDeleteModalVisible} />
            )}
            {activeTab === 'disablement' && (
                <DisablementModal handleCancel={handleCancel} isModalVisible={isModalVisible} modalType={modalType} selectedRowData={selectedRowData} disablementData={disablementData} setDisablementData={setDisablementData} setTableKey={setTableKey} setIsDeleteModalVisible={setIsDeleteModalVisible} isDeleteModalVisible={isDeleteModalVisible} />
            )}
            {activeTab === 'reason' && (
                <EsicReasonModal handleCancel={handleCancel} isModalVisible={isModalVisible} modalType={modalType} selectedRowData={selectedRowData} reasonData={reasonData} setReasonData={setReasonData} setTableKey={setTableKey} setIsDeleteModalVisible={setIsDeleteModalVisible} isDeleteModalVisible={isDeleteModalVisible} />
            )}
            {activeTab === 'benefit' && (
                <EsicBenefitModal handleCancel={handleCancel} isModalVisible={isModalVisible} modalType={modalType} selectedRowData={selectedRowData} benefitData={benefitData} setBenefitData={setBenefitData} setTableKey={setTableKey} setIsDeleteModalVisible={setIsDeleteModalVisible} isDeleteModalVisible={isDeleteModalVisible} />
            )}
        </>
    );
};

export default Configuration;
